import styled, { css } from 'styled-components';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import { Text } from '~/components/ui/Text';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	row-gap: 20px;
	margin-top: 30px;
`;

const StyledInfoContainer = styled.div`
	display: flex;
	flex-direction: column;
	row-gap: 20px;
`;

const StyledInfoRow = styled.div`
	display: grid;
	grid-template-columns: 20px 1fr auto;
	column-gap: 20px;
	opacity: 0;
	height: 0;
	transition:
		opacity 0.3s ease,
		height 0.3s ease;

	&.info-enter {
		opacity: 0;
		height: 0;
	}

	&.info-enter-active,
	&.info-enter-done,
	&.info-exit {
		opacity: 1;
		height: 20px;
	}

	&.info-exit-active {
		opacity: 0;
		height: 0;
	}

	${Text} {
		&:nth-child(2) {
			font-weight: 400;
			font-size: 16px;
			line-height: 18px;
			color: #ffffff;
			opacity: 0.25;
		}

		&:nth-child(3) {
			font-weight: 700;
			font-size: 16px;
			line-height: 18px;
			color: #00ff37;
		}
	}
`;

const StyledPlusContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	background-color: #1f8034;
	border-radius: 6px;

	img {
		width: 10px;
		height: 10px;
		user-select: none;
		pointer-events: none;
	}
`;

const StyledCostSectionContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
`;

const StyledCostContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	row-gap: 20px;
	width: 100%;
	height: 112px;
	background-color: rgba(0, 0, 0, 0.25);
	border-radius: 10px;

	& > ${Text} {
		font-weight: 400;
		font-size: 14px;
		line-height: 16px;
		color: #ffffff;
		opacity: 0.25;
	}
`;

const StyledPrice = styled.div`
	display: flex;
	align-items: center;
	column-gap: 10px;

	${Text} {
		font-weight: 500;
		font-size: 16px;
		line-height: 18px;
		color: #ffffff;
	}
`;

const StyledBuyButtonContent = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
`;

const StyledBuyButton = styled.div<{ $noinvert?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	column-gap: 6px;
	width: 100%;
	height: 50px;
	background-color: #1f8034;
	border-radius: 10px;
	cursor: pointer;
	transition: background-color 0.3s ease;

	${Text} {
		font-weight: 400;
		font-size: 16px;
		line-height: 18px;
		color: #ffffff;
		transition: color 0.3s ease;
	}

	img {
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
	}

	&:hover {
		background-color: #ffffff;

		${Text} {
			color: #000000;
		}

		${(props) =>
			!props.$noinvert &&
			css`
				img {
					filter: invert(100%);
				}
			`}
	}
`;

const StyledPlateContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const StyledArrow = styled.img.attrs({ src: arrowLeft })<{ $rotate?: boolean }>`
	width: 8px;
	height: 13px;
	user-select: none;
	pointer-events: none;
	transition:
		opacity 0.3s ease,
		filter 0.3s ease;

	${(props) =>
		props.$rotate &&
		css`
			transform: rotate(180deg);
			transform-origin: center;
		`}
`;

const StyledArrowContainer = styled.div<{ $disabled?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	border-radius: 30px;
	transition: background-color 0.3s ease;

	${(props) =>
		props.$disabled
			? css`
					background-color: rgba(255, 255, 255, 0.05);

					${StyledArrow} {
						opacity: 0.25;
					}
			  `
			: css`
					background-color: rgba(255, 255, 255, 0.1);
					cursor: pointer;

					${StyledArrow} {
						opacity: 1;
					}

					&:hover {
						background-color: #ffffff;

						${StyledArrow} {
							opacity: 1;
							filter: invert(100%);
						}
					}
			  `}
`;

const StyledRegionContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	column-gap: 20px;
`;

const StyledRegionName = styled(Text)`
	font-weight: 400;
	font-size: 16px;
	line-height: 18px;
	color: #ffffff;
	opacity: 0.5;
`;

const StyledBottomGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
`;

const StyledButtonContent = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;

	img {
		width: 14px;
		height: 14px;
	}
`;

const StyledRegionSelectButtonContent = styled.div<{
	$active?: boolean;
	$disabled?: boolean;
}>`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 10px;
	width: 100%;
	max-width: 100%;
	overflow: hidden;

	img {
		flex-shrink: 0;
		transition:
			filter 0.3s ease,
			opacity 0.3s ease;
	}

	${Text} {
		font-size: 16px;
		font-weight: ${({ $active }) => ($active ? 500 : 400)};
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		min-width: 0;
		color: rgba(255, 255, 255, ${({ $disabled }) => ($disabled ? 0.25 : 0.5)});
		transition: color 0.3s ease;

		.region-code {
			font-weight: inherit;
			color: #ffffff;
			transition: color 0.3s ease;
		}

		span {
			font-weight: 400;
		}
	}

	${({ $disabled }) =>
		!$disabled &&
		css`
			button:hover &,
			button[hover] & {
				${Text} {
					color: rgba(0, 0, 0, 0.5);

					.region-code {
						color: #000000;
					}
				}
			}
		`}
`;

export {
	StyledArrow,
	StyledArrowContainer,
	StyledBottomGrid,
	StyledButtonContent,
	StyledBuyButton,
	StyledBuyButtonContent,
	StyledContainer,
	StyledCostContainer,
	StyledCostSectionContainer,
	StyledInfoContainer,
	StyledInfoRow,
	StyledPlateContainer,
	StyledPlusContainer,
	StyledPrice,
	StyledRegionContainer,
	StyledRegionName,
	StyledRegionSelectButtonContent,
};
